export const MAX_VISIBLE_EVENTS_PER_DAY = 20;
export const weekdayLabels = ['日', '月', '火', '水', '木', '金', '土'];

export const formatYMD = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const startOfWeek = (date: Date) => {
  const result = new Date(date);
  result.setDate(result.getDate() - result.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
};

export const endOfWeek = (date: Date) => {
  const result = startOfWeek(date);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const addDays = (date: Date, amount: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

export const addMonths = (date: Date, amount: number) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + amount);
  return result;
};

export const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

export const formatYearMonthJP = (date: Date) => `${date.getFullYear()}年 ${date.getMonth() + 1}月`;

export const getCalendarGridRange = (month: Date) => ({
  startDate: formatYMD(startOfWeek(startOfMonth(month))),
  endDate: formatYMD(endOfWeek(endOfMonth(month))),
});

export const normalizeDateKey = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) return formatYMD(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const datePart = trimmed.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : formatYMD(parsed);
};

const formatCalendarTime = (value: unknown) => {
  const match = String(value ?? '').trim().match(/(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : '';
};

const decorateCalendarOccurrence = (event: any, key: string) => {
  const isAdditional = event?.occurrence_type === 'additional_date';
  const additionalTitle = isAdditional
    ? String(event?.additional_date_title || event?.performance_type || '').trim()
    : '';
  const timeText = isAdditional
    ? [formatCalendarTime(event?.start_time), formatCalendarTime(event?.end_time)]
        .filter(Boolean)
        .join('〜')
    : formatCalendarTime(event?.start_time);
  return {
    ...event,
    event_date: key,
    calendar_date_key: key,
    calendar_is_additional: isAdditional,
    calendar_additional_title: additionalTitle,
    calendar_title: isAdditional ? additionalTitle || '追加日程' : '本番',
    calendar_time: timeText,
    calendar_start_time: event?.start_time || '',
  };
};

export const buildEventsByDate = (list: any[]) => {
  const eventsByDate: Record<string, any[]> = {};
  if (!Array.isArray(list)) return eventsByDate;
  list.forEach((event) => {
    const occurrenceType = String(event?.occurrence_type || '').trim();
    if (['main', 'performance', 'additional_date'].includes(occurrenceType)) {
      const occurrenceDate = normalizeDateKey(event?.calendar_date_key || event?.event_date);
      if (!occurrenceDate) return;
      if (!eventsByDate[occurrenceDate]) eventsByDate[occurrenceDate] = [];
      eventsByDate[occurrenceDate].push(decorateCalendarOccurrence(event, occurrenceDate));
      return;
    }

    const mainDate = normalizeDateKey(event.event_date || event.date);
    const additionalByKey = new Map<string, any>();
    if (Array.isArray(event.additional_dates)) {
      event.additional_dates.forEach((item: any) => {
        const key = normalizeDateKey(item.additional_date || item.date || item);
        if (key && key !== mainDate && !additionalByKey.has(key)) {
          additionalByKey.set(key, item && typeof item === 'object' ? item : {});
        }
      });
    }
    const dates = new Set<string>();
    if (mainDate) dates.add(mainDate);
    additionalByKey.forEach((_, key) => dates.add(key));
    dates.forEach((key) => {
      if (!eventsByDate[key]) eventsByDate[key] = [];
      const isAdditional = key !== mainDate;
      const additional = isAdditional ? additionalByKey.get(key) : null;
      eventsByDate[key].push({
        ...event,
        event_date: key,
        calendar_date_key: key,
        calendar_is_additional: isAdditional,
        calendar_additional_title: additional
          ? String(additional.additional_date_title || additional.description || '').trim()
          : '',
        calendar_start_time: isAdditional ? (additional?.start_time || '') : (event.start_time || ''),
      });
    });
  });
  const startKey = (event: any) =>
    String(event?.calendar_start_time || '').slice(0, 8) || '99:99:99';
  Object.keys(eventsByDate).forEach((key) => {
    eventsByDate[key].sort((a, b) => startKey(a).localeCompare(startKey(b)));
  });
  return eventsByDate;
};

export const getCalendarEventChipClass = (
  event: any,
  { tooltip = false }: { tooltip?: boolean } = {}
) => [
  'event-chip',
  tooltip ? 'event-chip--has-tooltip' : '',
  event?.calendar_is_additional ? 'event-chip--additional' : '',
  event?.canceled_at ? 'status-cancelled' : '',
].filter(Boolean).join(' ');
